import { useEffect, useMemo, useState } from 'react';
import { Gift, Coins, Percent, History, TriangleAlert, Sparkles, Lock, Info } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Switch } from '@/components/ui/Switch';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { LoadingState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/cn';
import { getLoyaltySettings, updateLoyaltySettings } from '../api';
import type { LoyaltySettings, LoyaltySettingsInput } from '../types';

type Basis = LoyaltySettings['eligible_amount_basis'];

const BASIS_LABELS: Record<Basis, string> = {
  subtotal: 'Subtotal — before any discount',
  after_discount: 'After discount — what they paid for the goods (recommended)',
  final_total: 'Final total — including GST',
};

const rupees = (v: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(v || 0);

/** A worked example is the only reliable way to show what a rule actually does. */
const EXAMPLE_BILL = 1000;

/**
 * The loyalty programme's settings screen.
 *
 * The single most important thing this page has to communicate is that changing
 * a rule affects the NEXT sale and never re-prices an old one — every bill
 * permanently stores the points it earned and redeemed, and every ledger row
 * stores the amount it was calculated from. Staff who do not believe that will
 * be afraid to touch these fields at all.
 *
 * There is exactly one exception, and it is surfaced the moment the field that
 * causes it is edited rather than buried in a footnote: shortening the expiry
 * period CAN lapse points earned under the older, longer one.
 *
 * ── Why the whole page stays editable while the programme is off ──
 * A hospital configures the rules first and switches on afterwards. Disabling
 * the fields until it is on would force them to switch on a live programme
 * carrying the shipped defaults, which is the one state nobody wants. The off
 * state is therefore signalled, not enforced.
 */
export default function LoyaltyPointsPanel() {
  const [settings, setSettings] = useState<LoyaltySettings | null>(null);
  const [draft, setDraft] = useState<LoyaltySettingsInput>({});
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getLoyaltySettings()
      .then((s) => { setSettings(s); setDraft({}); })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)));
  }, []);

  const value = <K extends keyof LoyaltySettings>(key: K): LoyaltySettings[K] | undefined =>
    (draft as Record<string, unknown>)[key] !== undefined
      ? ((draft as Record<string, unknown>)[key] as LoyaltySettings[K])
      : settings?.[key];

  const set = (patch: LoyaltySettingsInput) => { setDraft((d) => ({ ...d, ...patch })); setSaved(false); };

  const persist = async (patch: LoyaltySettingsInput, spinner: (v: boolean) => void) => {
    spinner(true); setError(''); setSaved(false);
    try {
      const updated = await updateLoyaltySettings(patch);
      setSettings(updated);
      setDraft({});
      setSaved(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
    spinner(false);
  };

  const dirty = Object.keys(draft).length > 0;

  const earnAmount = Number(value('earn_amount') ?? 100);
  const earnPoints = Number(value('earn_points') ?? 1);
  const pointValue = Number(value('point_value') ?? 1);
  const maxPercent = Number(value('max_redeem_percent') ?? 50);
  const maxAmount = value('max_redeem_amount');
  const minBalance = Number(value('min_balance_to_redeem') ?? 100);
  const expiryMonths = Number(value('expiry_months') ?? 12);

  // The example runs the real rules in the real order, so the preview and the
  // counter can never tell the admin two different stories.
  const example = useMemo(() => {
    const earned = Math.floor((EXAMPLE_BILL / (earnAmount || 1)) * (earnPoints || 0));
    const capByPercent = Math.floor((EXAMPLE_BILL * (maxPercent / 100)) / (pointValue || 1));
    const capByAmount = maxAmount != null ? Math.floor(maxAmount / (pointValue || 1)) : Infinity;
    const redeemable = Math.max(0, Math.min(capByPercent, capByAmount, Math.floor(EXAMPLE_BILL / (pointValue || 1))));
    const binding = capByAmount < capByPercent ? `the ${rupees(maxAmount as number)} ceiling` : `the ${maxPercent}% cap`;
    return { earned, redeemable, binding };
  }, [earnAmount, earnPoints, pointValue, maxPercent, maxAmount]);

  const shorteningExpiry = settings != null && settings.expiry_months !== 0 && expiryMonths !== 0 && expiryMonths < settings.expiry_months;
  const introducingExpiry = settings != null && settings.expiry_months === 0 && expiryMonths > 0;

  if (!settings) {
    return error
      ? <div className="rounded-lg bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>
      : <LoadingState />;
  }

  const enabled = settings.enabled;

  return (
    <div className="space-y-4 pb-24" style={{ maxWidth: 900 }}>
      {error && <div className="rounded-lg bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}

      {/* ── The switch, and what it means ────────────────────────── */}
      <Card className={cn('transition-colors', enabled && 'border-teal')}>
        <div className="flex flex-wrap items-start gap-4">
          <span
            className={cn(
              'grid h-11 w-11 shrink-0 place-content-center rounded-full',
              enabled ? 'bg-accent text-teal' : 'bg-muted text-ink-muted'
            )}
          >
            <Gift size={21} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">Loyalty points</div>
            <p className="mt-1 max-w-xl text-[12.5px] leading-relaxed text-ink-muted">
              Rewards repeat pharmacy customers: they earn points on what they buy at the{' '}
              <b>walk-in counter</b>, and can spend those points as a discount on a later purchase.
            </p>
            <p className={cn('mt-2 text-[12px] font-medium', enabled ? 'text-success-ink' : 'text-ink-muted')}>
              {enabled
                ? `On — ₹${earnAmount} spent earns ${earnPoints} point${earnPoints === 1 ? '' : 's'}, and 1 point is worth ${rupees(pointValue)}.`
                : 'Off — no points are earned or redeemed, and the points panel is hidden at the counter.'}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2.5">
            <span className="text-[12px] font-semibold text-ink-muted">{enabled ? 'On' : 'Off'}</span>
            <Switch checked={enabled} disabled={toggling} onCheckedChange={(v) => persist({ enabled: v }, setToggling)} />
          </div>
        </div>

        {!enabled && (
          <div className="mt-3.5 flex gap-2.5 rounded-lg bg-info-soft px-3 py-2.5 text-[11.5px] text-info-ink">
            <Info size={15} className="mt-px shrink-0" />
            <p className="leading-relaxed">
              Set the rules below first, then switch on — the settings stay editable while the
              programme is off. Existing balances and old bills are never affected either way.
            </p>
          </div>
        )}
      </Card>

      <div className="grid items-start gap-4 min-[900px]:grid-cols-2">
        {/* ── Earning ───────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle><Coins size={15} /> Earning</CardTitle>
          </CardHeader>

          <div className="mb-3.5 rounded-lg bg-canvas p-3">
            {/* The rule read back as the sentence staff will say to a customer,
                rather than as three disconnected inputs. */}
            <div className="flex flex-wrap items-center gap-2 text-[13px]">
              <span className="text-ink-muted">Every</span>
              <div className="relative">
                <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px] text-ink-muted">₹</span>
                <Input
                  type="number"
                  min={1}
                  className="h-8 w-24 pl-6 tabular-nums"
                  value={earnAmount}
                  onChange={(e) => set({ earn_amount: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <span className="text-ink-muted">spent earns</span>
              <Input
                type="number"
                min={0}
                step="0.01"
                className="h-8 w-20 tabular-nums"
                value={earnPoints}
                onChange={(e) => set({ earn_points: parseFloat(e.target.value) || 0 })}
              />
              <span className="text-ink-muted">point{earnPoints === 1 ? '' : 's'}.</span>
            </div>
          </div>

          <div className="space-y-3">
            <FormField label="Calculated on">
              <NativeSelect
                value={value('eligible_amount_basis')}
                onChange={(e) => set({ eligible_amount_basis: e.target.value as Basis })}
              >
                {(Object.keys(BASIS_LABELS) as Basis[]).map((k) => (
                  <option key={k} value={k}>{BASIS_LABELS[k]}</option>
                ))}
              </NativeSelect>
            </FormField>

            <FormField label="One point is worth" labelExtra={<span className="text-[10.5px] text-ink-muted">₹ off a later bill</span>}>
              <div className="relative">
                <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px] text-ink-muted">₹</span>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  className="w-32 pl-6 tabular-nums"
                  value={pointValue}
                  onChange={(e) => set({ point_value: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </FormField>
          </div>

          <p className="mt-3 border-t border-line pt-2.5 text-[11.5px] text-ink-muted">
            Points are whole numbers and always round <b>down</b> — at ₹100 = 1 point, a ₹190 bill
            earns 1 point, not 2.
          </p>
        </Card>

        {/* ── Redeeming ─────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle><Percent size={15} /> Redeeming</CardTitle>
          </CardHeader>

          <p className="-mt-1.5 mb-3.5 text-[12px] leading-relaxed text-ink-muted">
            All three limits apply together and the <b>smallest one wins</b>. The counter is told
            which one applied, so staff can explain it rather than just refusing.
          </p>

          <div className="space-y-3">
            <FormField label="Most that can come off one bill" labelExtra={<span className="text-[10.5px] text-ink-muted">% of the bill</span>}>
              <div className="relative">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  className="w-32 pr-7 tabular-nums"
                  value={maxPercent}
                  onChange={(e) => set({ max_redeem_percent: parseFloat(e.target.value) || 0 })}
                />
                <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[12px] text-ink-muted">%</span>
              </div>
            </FormField>

            <FormField label="Ceiling per bill" labelExtra={<span className="text-[10.5px] text-ink-muted">blank = no ceiling</span>}>
              <div className="relative">
                <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px] text-ink-muted">₹</span>
                <Input
                  type="number"
                  min={0}
                  className="w-40 pl-6 tabular-nums"
                  value={maxAmount ?? ''}
                  placeholder="No ceiling"
                  onChange={(e) => set({ max_redeem_amount: e.target.value === '' ? null : parseFloat(e.target.value) })}
                />
              </div>
            </FormField>

            <FormField label="Minimum balance before points can be used at all" labelExtra={<span className="text-[10.5px] text-ink-muted">points</span>}>
              <Input
                type="number"
                min={0}
                className="w-32 tabular-nums"
                value={minBalance}
                onChange={(e) => set({ min_balance_to_redeem: parseInt(e.target.value, 10) || 0 })}
              />
            </FormField>
          </div>
        </Card>
      </div>

      {/* ── Worked example ──────────────────────────────────────── */}
      <Card className="border-teal/30 bg-accent/40">
        <CardHeader>
          <CardTitle><Sparkles size={15} /> On a {rupees(EXAMPLE_BILL)} purchase</CardTitle>
        </CardHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-border bg-card px-3.5 py-3">
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">They earn</div>
            <div className="mt-1 text-[22px] font-bold leading-none">
              {example.earned} <span className="text-[13px] font-semibold text-ink-muted">point{example.earned === 1 ? '' : 's'}</span>
            </div>
            <div className="mt-1 text-[11.5px] text-ink-muted">worth {rupees(example.earned * pointValue)} later</div>
          </div>
          <div className="rounded-lg border border-border bg-card px-3.5 py-3">
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">They could spend up to</div>
            <div className="mt-1 text-[22px] font-bold leading-none">
              {example.redeemable} <span className="text-[13px] font-semibold text-ink-muted">point{example.redeemable === 1 ? '' : 's'}</span>
            </div>
            <div className="mt-1 text-[11.5px] text-ink-muted">
              {rupees(example.redeemable * pointValue)} off — limited by {example.binding}
            </div>
          </div>
        </div>
        <p className="mt-3 text-[11.5px] text-ink-muted">
          Assuming a balance large enough to cover it, and at least the {minBalance}-point minimum.
        </p>
      </Card>

      {/* ── Expiry ──────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle><History size={15} /> Expiry</CardTitle>
        </CardHeader>

        <div className="flex flex-wrap items-end gap-4">
          <FormField label="Unused points expire after" labelExtra={<span className="text-[10.5px] text-ink-muted">months · 0 = never</span>}>
            <Input
              type="number"
              min={0}
              className="w-32 tabular-nums"
              value={expiryMonths}
              onChange={(e) => set({ expiry_months: parseInt(e.target.value, 10) || 0 })}
            />
          </FormField>
          <p className="min-w-[240px] flex-1 pb-1 text-[12px] leading-relaxed text-ink-muted">
            Points are earned in batches, each with its own expiry date, and spending always uses the
            batch expiring <b>soonest</b> first — so a customer never loses points that were about to
            lapse while newer ones sat unused.
          </p>
        </div>

        {(shorteningExpiry || introducingExpiry) && (
          <div className="mt-3 flex gap-2.5 rounded-lg bg-warn-soft px-3 py-2.5 text-[11.5px] text-caution-ink">
            <TriangleAlert size={15} className="mt-px shrink-0" />
            <p className="leading-relaxed">
              <b>Expiry is the one setting on this page that reaches backwards.</b>{' '}
              {introducingExpiry
                ? 'Points earned while nothing expired will be given an expiry date, and any already older than this window will lapse the next time that customer’s balance is read.'
                : `Shortening this from ${settings.expiry_months} months can lapse points earned under the longer window, the next time that customer’s balance is read.`}
            </p>
          </div>
        )}
      </Card>

      {/* ── History guarantee ───────────────────────────────────── */}
      <div className="flex gap-2.5 rounded-lg border border-border px-3.5 py-3 text-[11.5px] leading-relaxed text-ink-muted">
        <Lock size={15} className="mt-px shrink-0" />
        <p>
          <b className="text-ink">History never changes.</b> Every rule here applies to the{' '}
          <b>next</b> sale. Bills already issued keep the points they earned and redeemed, and every
          ledger entry keeps the amount it was calculated from — nothing on this page re-prices an
          old purchase.
          {settings.updated_by && (
            <> Last changed by {settings.updated_by}
              {settings.updated_at ? ` on ${new Date(settings.updated_at).toLocaleString('en-IN')}` : ''}.</>
          )}
        </p>
      </div>

      {/* ── Save bar ────────────────────────────────────────────── */}
      {/* Sticky rather than a button at the bottom of a long form: the rules are
          spread over four cards, and a save control that scrolls away is how
          edits get abandoned half-applied. */}
      {(dirty || saved) && (
        <div className="sticky bottom-4 z-10">
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 shadow-card">
            {dirty ? (
              <>
                <span className="text-[12.5px] font-medium">Unsaved changes</span>
                <div className="ml-auto flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setDraft({}); setSaved(false); }}>Discard</Button>
                  <Button size="sm" disabled={saving} loading={saving} onClick={() => persist(draft, setSaving)}>
                    {saving ? 'Saving…' : 'Save loyalty rules'}
                  </Button>
                </div>
              </>
            ) : (
              <span className="text-[12.5px] font-medium text-success-ink">✓ Saved.</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
